import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { MediaService } from '../../../../services/media.service';
import { ToastService } from '../../../../services/toast.service';

interface PageHero {
  id: number;
  pageKey: string;
  eyebrowText: string | null;
  title: string;
  accentText: string | null;
  subtitle: string | null;
  backgroundType: string;
  mediaAssetId: number | null;
  fallbackMediaAssetId: number | null;
}

interface PageHeroDraft {
  eyebrowText: string;
  title: string;
  accentText: string;
  subtitle: string;
  mediaAssetId: number | null;
}

interface AboutSection {
  id: number;
  title: string;
  body: string;
  mediaAssetId: number | null;
  sortOrder: number;
  isActive: boolean;
}

interface AboutSectionDraft {
  title: string;
  body: string;
  mediaAssetId: number | null;
  isActive: boolean;
}

interface NewAboutSectionDraft {
  title: string;
  body: string;
  isActive: boolean;
}

interface ReorderAboutSectionsRequest {
  sectionIds: number[];
}

@Component({
  selector: 'app-admin-about',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-about.component.html',
  styleUrls: [
    './admin-about.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminAboutComponent implements OnInit, OnDestroy {
  private readonly apiUrl = `${environment.apiUrl}/about`;
  private readonly heroApiUrl = `${environment.apiUrl}/pageHeroes/about`;

  hero: PageHero | null = null;

  heroDraft: PageHeroDraft = {
    eyebrowText: '',
    title: '',
    accentText: '',
    subtitle: '',
    mediaAssetId: null
  };

  heroLoading = true;
  heroSaving = false;

  heroImage: File | null = null;
  heroPreview: string | null = null;

  sections: AboutSection[] = [];
  drafts = new Map<number, AboutSectionDraft>();

  loading = true;
  creating = false;
  showCreateForm = false;

  savingSectionIds = new Set<number>();
  deletingSectionIds = new Set<number>();
  pendingDeleteSectionId: number | null = null;

  selectedImages = new Map<number, File>();
  localPreviews = new Map<number, string>();

  newSection: NewAboutSectionDraft = this.createEmptySection();
  newSectionImage: File | null = null;
  newSectionPreview: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly mediaService: MediaService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef)
  {
  }

  ngOnInit(): void {
    this.loadHero();
    this.loadSections();
  }

  ngOnDestroy(): void {
    for (const preview of this.localPreviews.values())
      URL.revokeObjectURL(preview);

    if (this.newSectionPreview)
      URL.revokeObjectURL(this.newSectionPreview);

    if (this.heroPreview)
      URL.revokeObjectURL(this.heroPreview);
  }

  // =========================================================
  // HERO
  // =========================================================

  async loadHero(): Promise<void> {
    this.heroLoading = true;
    this.cdr.detectChanges();

    try {
      this.hero = await firstValueFrom(
        this.http.get<PageHero>(
          this.heroApiUrl,
          { withCredentials: true })
      );

      this.heroDraft = {
        eyebrowText: this.hero.eyebrowText ?? '',
        title: this.hero.title,
        accentText: this.hero.accentText ?? '',
        subtitle: this.hero.subtitle ?? '',
        mediaAssetId: this.hero.mediaAssetId
      };
    }
    catch {
      this.toastService.error('Unable to load About page hero.');
    }

    this.heroLoading = false;
    this.cdr.detectChanges();
  }

  onHeroImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';

    if (!file)
      return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error('Please select an image file.');
      return;
    }

    if (!this.checkHeroImageSize(file)) return;

    this.clearHeroPreview();

    this.heroImage = file;
    this.heroPreview = URL.createObjectURL(file);

    this.cdr.detectChanges();
  }

  removeHeroImage(): void {
    this.heroImage = null;
    this.clearHeroPreview();

    this.heroDraft.mediaAssetId = null;

    this.cdr.detectChanges();
  }

  async saveHero(): Promise<void> {
    if (this.heroSaving) return;
    if (this.heroImage && !this.checkHeroImageSize(this.heroImage)) return;

    if (!this.hero)
      return;

    const title = this.heroDraft.title.trim();

    if (!title) {
      this.toastService.error('Hero title is required.');
      return;
    }

    this.heroSaving = true;
    this.cdr.detectChanges();

    try {
      let mediaAssetId = this.heroDraft.mediaAssetId;

      if (this.heroImage) {
        const media = await firstValueFrom(
          this.mediaService.uploadImage(this.heroImage)
        );

        mediaAssetId = media.id;
      }

      const saved = await firstValueFrom(
        this.http.put<PageHero>(
          this.heroApiUrl,
          {
            eyebrowText: this.heroDraft.eyebrowText.trim() || null,
            title,
            accentText: this.heroDraft.accentText.trim() || null,
            subtitle: this.heroDraft.subtitle.trim() || null,
            backgroundType: 0, //enum
            mediaAssetId,
            fallbackMediaAssetId: this.hero.fallbackMediaAssetId
          },
          { withCredentials: true })
      );

      this.hero = saved;

      this.heroDraft = {
        eyebrowText: saved.eyebrowText ?? '',
        title: saved.title,
        accentText: saved.accentText ?? '',
        subtitle: saved.subtitle ?? '',
        mediaAssetId: saved.mediaAssetId
      };

      this.heroImage = null;
      this.clearHeroPreview();

      this.toastService.success('About hero saved.');
    }
    catch (error: unknown) {
      const response = error as { error?: string | { message?: string }; status?: number };
      const message = typeof response.error === 'string' && response.error.trim()
        ? response.error
        : typeof response.error === 'object' && response.error?.message
          ? response.error.message
          : response.status === 413 ? 'Image must be 5 MB or smaller.' : 'Unable to save About hero.';
      this.toastService.error(message);
    }

    this.heroSaving = false;
    this.cdr.detectChanges();
  }

  resetHero(): void {
    if (!this.hero)
      return;

    this.heroImage = null;
    this.clearHeroPreview();

    this.heroDraft = {
      eyebrowText: this.hero.eyebrowText ?? '',
      title: this.hero.title,
      accentText: this.hero.accentText ?? '',
      subtitle: this.hero.subtitle ?? '',
      mediaAssetId: this.hero.mediaAssetId
    };

    this.cdr.detectChanges();
  }

  private checkHeroImageSize(file: File): boolean {
    if (file.size <= 5 * 1024 * 1024) return true;
    this.toastService.error('Image must be 5 MB or smaller.');
    return false;
  }

  getHeroImageUrl(): string | null {
    if (this.heroPreview)
      return this.heroPreview;

    if (!this.heroDraft.mediaAssetId)
      return null;

    return `${environment.apiUrl}/media/${this.heroDraft.mediaAssetId}`;
  }

  heroHasChanges(): boolean {
    if (!this.hero)
      return false;

    return (
      this.heroDraft.eyebrowText !== (this.hero.eyebrowText ?? '') ||
      this.heroDraft.title !== this.hero.title ||
      this.heroDraft.accentText !== (this.hero.accentText ?? '') ||
      this.heroDraft.subtitle !== (this.hero.subtitle ?? '') ||
      this.heroDraft.mediaAssetId !== this.hero.mediaAssetId ||
      this.heroImage !== null
    );
  }

  private clearHeroPreview(): void {
    if (this.heroPreview)
      URL.revokeObjectURL(this.heroPreview);

    this.heroPreview = null;
  }

  // =========================================================
  // LOAD SECTIONS
  // =========================================================

  async loadSections(): Promise<void> {
    this.loading = true;
    this.cdr.detectChanges();

    try {
      const sections = await firstValueFrom(
        this.http.get<AboutSection[]>(
          `${this.apiUrl}/admin`,
          { withCredentials: true })
      );

      this.sections = sections
        .sort((a, b) => a.sortOrder - b.sortOrder);

      this.rebuildDrafts();
    }
    catch {
      this.toastService.error('Unable to load About page sections.');
    }

    this.loading = false;
    this.cdr.detectChanges();
  }

  // =========================================================
  // CREATE SECTION
  // =========================================================

  openCreateForm(): void {
    this.showCreateForm = true;
    this.cdr.detectChanges();
  }

  cancelCreate(): void {
    this.resetNewSection();
    this.showCreateForm = false;

    this.cdr.detectChanges();
  }

  onNewImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';

    if (!file)
      return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error('Please select an image file.');
      return;
    }

    if (this.newSectionPreview)
      URL.revokeObjectURL(this.newSectionPreview);

    this.newSectionImage = file;
    this.newSectionPreview = URL.createObjectURL(file);

    this.cdr.detectChanges();
  }

  removeNewImage(): void {
    if (this.newSectionPreview)
      URL.revokeObjectURL(this.newSectionPreview);

    this.newSectionImage = null;
    this.newSectionPreview = null;

    this.cdr.detectChanges();
  }

  async createSection(): Promise<void> {
    const title = this.newSection.title.trim();
    const body = this.newSection.body.trim();

    if (!title) {
      this.toastService.error('Section title is required.');
      return;
    }

    if (!body) {
      this.toastService.error('Section content is required.');
      return;
    }

    this.creating = true;
    this.cdr.detectChanges();

    try {
      let mediaAssetId: number | null = null;

      if (this.newSectionImage) {
        const media = await firstValueFrom(
          this.mediaService.uploadImage(this.newSectionImage)
        );

        mediaAssetId = media.id;
      }

      await firstValueFrom(
        this.http.post<AboutSection>(
          this.apiUrl,
          {
            title,
            body,
            mediaAssetId,
            isActive: this.newSection.isActive
          },
          { withCredentials: true })
      );

      this.toastService.success('About section created.');

      this.resetNewSection();
      this.showCreateForm = false;

      await this.loadSections();
    }
    catch {
      this.toastService.error('Unable to create About section.');
    }

    this.creating = false;
    this.cdr.detectChanges();
  }

  // =========================================================
  // EDIT SECTION
  // =========================================================

  getDraft(sectionId: number): AboutSectionDraft {
    return this.drafts.get(sectionId)!;
  }

  onImageSelected(sectionId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';

    if (!file)
      return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error('Please select an image file.');
      return;
    }

    this.clearLocalPreview(sectionId);

    this.selectedImages.set(sectionId, file);
    this.localPreviews.set(
      sectionId,
      URL.createObjectURL(file)
    );

    this.cdr.detectChanges();
  }

  removeImage(sectionId: number): void {
    this.selectedImages.delete(sectionId);
    this.clearLocalPreview(sectionId);

    const draft = this.getDraft(sectionId);
    draft.mediaAssetId = null;

    this.cdr.detectChanges();
  }

  async saveSection(section: AboutSection): Promise<void> {
    const draft = this.getDraft(section.id);

    const title = draft.title.trim();
    const body = draft.body.trim();

    if (!title) {
      this.toastService.error('Section title is required.');
      return;
    }

    if (!body) {
      this.toastService.error('Section content is required.');
      return;
    }

    this.savingSectionIds.add(section.id);
    this.cdr.detectChanges();

    try {
      let mediaAssetId = draft.mediaAssetId;

      const selectedImage =
        this.selectedImages.get(section.id);

      if (selectedImage) {
        const media = await firstValueFrom(
          this.mediaService.uploadImage(selectedImage)
        );

        mediaAssetId = media.id;
      }

      const saved = await firstValueFrom(
        this.http.put<AboutSection>(
          `${this.apiUrl}/${section.id}`,
          {
            title,
            body,
            mediaAssetId,
            isActive: draft.isActive
          },
          { withCredentials: true })
      );

      const index = this.sections
        .findIndex(x => x.id === section.id);

      if (index >= 0)
        this.sections[index] = saved;

      this.selectedImages.delete(section.id);
      this.clearLocalPreview(section.id);

      this.drafts.set(section.id, {
        title: saved.title,
        body: saved.body,
        mediaAssetId: saved.mediaAssetId,
        isActive: saved.isActive
      });

      this.toastService.success('About section saved.');
    }
    catch {
      this.toastService.error('Unable to save About section.');
    }

    this.savingSectionIds.delete(section.id);
    this.cdr.detectChanges();
  }

  resetSection(section: AboutSection): void {
    this.selectedImages.delete(section.id);
    this.clearLocalPreview(section.id);

    this.drafts.set(section.id, {
      title: section.title,
      body: section.body,
      mediaAssetId: section.mediaAssetId,
      isActive: section.isActive
    });

    this.cdr.detectChanges();
  }

  // =========================================================
  // DELETE SECTION
  // =========================================================

  requestDeleteSection(section: AboutSection): void {
    if (this.isDeleting(section.id)) return;
    this.pendingDeleteSectionId = section.id;
  }

  cancelDeleteSection(): void {
    this.pendingDeleteSectionId = null;
  }

  async deleteSection(section: AboutSection): Promise<void> {
    if (this.pendingDeleteSectionId !== section.id || this.isDeleting(section.id)) return;

    this.deletingSectionIds.add(section.id);
    this.cdr.detectChanges();

    try {
      await firstValueFrom(
        this.http.delete(
          `${this.apiUrl}/${section.id}`,
          { withCredentials: true })
      );

      this.selectedImages.delete(section.id);
      this.clearLocalPreview(section.id);
      this.drafts.delete(section.id);

      this.sections = this.sections
        .filter(x => x.id !== section.id)
        .map((item, index) => ({
          ...item,
          sortOrder: index + 1
        }));

      this.toastService.success('About section deleted.');
      this.pendingDeleteSectionId = null;
    }
    catch {
      this.toastService.error('Unable to delete About section.');
    }

    this.deletingSectionIds.delete(section.id);
    this.cdr.detectChanges();
  }

  // =========================================================
  // REORDER
  // =========================================================

  async moveUp(index: number): Promise<void> {
    if (index <= 0)
      return;

    await this.moveSection(index, index - 1);
  }

  async moveDown(index: number): Promise<void> {
    if (index >= this.sections.length - 1)
      return;

    await this.moveSection(index, index + 1);
  }

  private async moveSection(
    fromIndex: number,
    toIndex: number): Promise<void>
  {
    const previousOrder = [...this.sections];

    const reordered = [...this.sections];
    const [section] = reordered.splice(fromIndex, 1);

    reordered.splice(toIndex, 0, section);

    this.sections = reordered.map(
      (item, index) => ({
        ...item,
        sortOrder: index + 1
      })
    );

    this.cdr.detectChanges();

    const request: ReorderAboutSectionsRequest = {
      sectionIds: this.sections.map(x => x.id)
    };

    try {
      await firstValueFrom(
        this.http.put(
          `${this.apiUrl}/reorder`,
          request,
          { withCredentials: true })
      );
    }
    catch {
      this.sections = previousOrder;

      this.toastService.error(
        'Unable to reorder About sections.'
      );

      this.cdr.detectChanges();
    }
  }

  // =========================================================
  // HELPERS
  // =========================================================

  getImageUrl(section: AboutSection): string | null {
    const localPreview =
      this.localPreviews.get(section.id);

    if (localPreview)
      return localPreview;

    const draft = this.getDraft(section.id);

    if (!draft.mediaAssetId)
      return null;

    return `${environment.apiUrl}/media/${draft.mediaAssetId}`;
  }

  hasChanges(section: AboutSection): boolean {
    const draft = this.getDraft(section.id);

    return (
      draft.title !== section.title ||
      draft.body !== section.body ||
      draft.mediaAssetId !== section.mediaAssetId ||
      draft.isActive !== section.isActive ||
      this.selectedImages.has(section.id)
    );
  }

  isSaving(sectionId: number): boolean {
    return this.savingSectionIds.has(sectionId);
  }

  isDeleting(sectionId: number): boolean {
    return this.deletingSectionIds.has(sectionId);
  }

  trackBySectionId(
    _index: number,
    section: AboutSection): number
  {
    return section.id;
  }

  private rebuildDrafts(): void {
    this.drafts.clear();

    for (const section of this.sections) {
      this.drafts.set(section.id, {
        title: section.title,
        body: section.body,
        mediaAssetId: section.mediaAssetId,
        isActive: section.isActive
      });
    }
  }

  private clearLocalPreview(sectionId: number): void {
    const preview =
      this.localPreviews.get(sectionId);

    if (preview)
      URL.revokeObjectURL(preview);

    this.localPreviews.delete(sectionId);
  }

  private resetNewSection(): void {
    if (this.newSectionPreview)
      URL.revokeObjectURL(this.newSectionPreview);

    this.newSection = this.createEmptySection();
    this.newSectionImage = null;
    this.newSectionPreview = null;
  }

  private createEmptySection(): NewAboutSectionDraft {
    return {
      title: '',
      body: '',
      isActive: true
    };
  }
}
